import NiceModal from '@ebay/nice-modal-react';
import 'swiper/css';
import 'swiper/css/pagination';

import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import StatisticsDataCards from 'components/charts/StatisticsDataCards';
import Button from 'components/field/Button';
import { NonFormContainer } from 'components/form/FormLayout.styled';
import Authorization from 'components/global/Authorization';
import PageHeader from 'components/global/PageHeader';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { FrequentlyAskedQuestion } from 'pages/Settings/Payment/FrequentlyAskedQuestion';

import { COD_WALLET } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons';

import * as S from './CODWallet.styled';
import CODWalletList from './CODWalletList/CODWalletList';
import CODWithdrawModal from './CODWalletList/Modals/CODWithdrawModal';

/**
 * @author @muhammedburakak
 */

const CODWallet = () => {
	const { showInfo } = useAlert();
	const navigate = useNavigate();
	const { getLocaleDate } = useLocale();

	const {
		remainingAmount,
		generalSettings: { BankName, BankHolderName, BankIban, address }
	} = useSelector((state) => state.user);

	const StatisticsUpdate = useSelector((state) => state.dynamicListData[DYNAMICLISTS.COD_WALLET_LIST]?.update);

	const [triggerCount, setTriggerCount] = useState(0);

	useUpdateEffect(() => {
		if (StatisticsUpdate) setTriggerCount((prev) => prev + 1);
	}, [StatisticsUpdate]);

	useEffect(() => {
		if (!BankName || !BankHolderName || !BankIban) {
			showInfo({
				header: 'infoMsg.notSetBankDetailsHeader',
				message: 'infoMsg.notSetBankDetailsMessage',
				link: 'infoMsg.notSetBankDetailsLink',
				linkAction: () => {
					navigate(PATHS.GENERAL_SETTINGS);
				}
			});
		}
		if (!address?.text) {
			showInfo({
				header: 'infoMsg.billingAddressNullHeader',
				message: 'infoMsg.billingAddressCannotBeNull',
				link: 'infoMsg.goToCompanySettings',
				linkAction: () => {
					navigate(PATHS.GENERAL_SETTINGS);
				}
			});
		}
	}, [BankName, BankHolderName, BankIban]);

	const isWithdrawDisabled = useMemo(
		() => !BankName || !BankHolderName || !BankIban || !address?.text || remainingAmount < 0,
		[BankName, BankHolderName, BankIban, address, remainingAmount]
	);

	const onShowExpectedPaymentDate = (data) => {
		if (!data?.expectedPaymentDate) return null;

		return (
			<Typography variant="p3" color="#5B6271">
				<Trans
					i18nKey="dashboard.expectedPaymentDate"
					values={{
						expectedPaymentDate: getLocaleDate(data?.expectedPaymentDate)
					}}
					components={{
						blackText: <S.BlackText />
					}}
				/>
			</Typography>
		);
	};

	const dataCardConfigs = [
		{
			accessor: 'totalAmountToBePaid',
			customIcon: 'fa-regular fa-coins',
			label: 'dashboard.statistic.totalAmountToBePaid',
			onShowFooter: onShowExpectedPaymentDate
		},
		{
			accessor: 'currentBalance',
			customIcon: 'fa-regular fa-wallet',
			label: 'dashboard.statistic.currentCodBalance'
		},
		{
			accessor: 'withdrawals',
			customIcon: 'fa-regular fa-building-columns',
			label: 'dashboard.statistic.withdrawals'
		}
	];

	return (
		<S.Container>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/CODashboard-icon.json.gz"
				title="CODWallet"
				tooltip="CODWallet.tooltip">
				<Authorization path="/modal/cod-withdraw-modal">
					<Button
						icon={faMoneyBillTransfer}
						label="withdraw"
						size="large"
						disabled={isWithdrawDisabled}
						disabledTooltip={
							remainingAmount < 0
								? 'CODWallet.withdrawDisabledMinusAmount'
								: 'CODWallet.withdrawDisabledTooltip'
						}
						onClick={() => NiceModal.show(CODWithdrawModal)}
						variant="blue"
					/>
				</Authorization>
			</PageHeader>

			<StatisticsDataCards
				key={triggerCount}
				fetchURL={COD_WALLET.GET_COD_WALLET_DASHBOARD_OVERVIEW}
				cardConfigs={dataCardConfigs}
			/>

			<div className="mt-5 mb-5 ">
				<CODWalletList />
			</div>

			<div>
				<Typography fontWeight="bold" variant="h3" css={S.FAQHeadingCss} className="mt-5" noWrap>
					CODWalletFAQs.frequentlyAskedQuestionsHeader
				</Typography>

				<Typography variant="p2" css={S.FAQSubtitleCss} noWrap>
					CODWalletFAQs.frequentlyAskedQuestionsSubtitle
				</Typography>
			</div>
			<NonFormContainer className="faqs">
				<FrequentlyAskedQuestion formName="CODWalletFAQ" />
			</NonFormContainer>
		</S.Container>
	);
};

export default CODWallet;
